import React from "react";

import { useLocalization } from "@fluent/react";
import { Button, Drawer, LoadingSkeleton } from "@narmi/design_system";

import { useReceipt } from "../../hooks/useReceipt";
import { getReasonCopy } from "../../utils/reasonCopy";
import ReceiptDetailsCard from "../ReceiptDetailsDrawer/ReceiptDetailsCard";
import ReceiptImagePreview from "../ReceiptDetailsDrawer/ReceiptImagePreview";

import styles from "./UnmatchedReceiptDrawer.module.scss";

import type { ReceiptListRow } from "../../hooks/useReceiptsList";

// Separate from MatchedReceiptDrawer: unmatched flow has no Unlink footer and
// surfaces the Suggested-match panel via the reasonCopy helper, which depends
// on list-row data (no_match_reason + retry_expires_at) the detail payload
// doesn't carry. Mirrors MatchedReceiptDrawer's structure so future refactors
// can fold both into a shared shell.
interface UnmatchedReceiptDrawerProps {
  /** UUID of the unmatched receipt to display, or null to keep the drawer unmounted. */
  uuid: string | null;
  /** UUIDs of the unmatched rows in display order — drives prev/next navigation. */
  uuids: string[];
  /** Select another receipt (prev/next navigation). */
  onSelect: (uuid: string) => void;
  /** The list row for the selected receipt — drives the Suggested-match panel copy. */
  row: ReceiptListRow | null;
  /** Injected wall-clock for deterministic retry-countdown rendering in tests. */
  now?: Date;
  onClose: () => void;
}

interface OpenDrawerProps {
  uuid: string;
  uuids: string[];
  onSelect: (uuid: string) => void;
  row: ReceiptListRow | null;
  now: Date;
  onClose: () => void;
}

const OpenDrawer = ({
  uuid,
  uuids,
  onSelect,
  row,
  now,
  onClose,
}: OpenDrawerProps) => {
  const { l10n } = useLocalization();
  const { data, isLoading, isError, refetch } = useReceipt(uuid);

  const currentIndex = uuids.indexOf(uuid);
  const prevUuid = currentIndex > 0 ? uuids[currentIndex - 1] : null;
  const nextUuid =
    currentIndex >= 0 && currentIndex < uuids.length - 1
      ? uuids[currentIndex + 1]
      : null;
  const handlePrev = prevUuid ? () => onSelect(prevUuid) : null;
  const handleNext = nextUuid ? () => onSelect(nextUuid) : null;

  const renderBody = () => {
    if (isLoading) {
      return (
        <div data-testid="unmatched-receipt-drawer-loading">
          <LoadingSkeleton isLoading content="headerText" size="large" />
          <LoadingSkeleton isLoading content="paragraph" lines={4} />
        </div>
      );
    }

    if (isError || !data) {
      return (
        <div
          className={styles.error}
          data-testid="unmatched-receipt-drawer-error"
        >
          <p className="fontColor--primary">
            {l10n.getString("receipts-drawer-error-load")}
          </p>
          <Button
            kind="secondary"
            label={l10n.getString("receipts-drawer-error-retry")}
            onClick={() => refetch()}
            testId="unmatched-receipt-drawer-retry"
          />
        </div>
      );
    }

    // Fall back to a list-row stub so the helper can render the bare no-match
    // copy when the drawer is opened in a state where the row is no longer in
    // the cached list (cache invalidation race).
    const reasonRow: ReceiptListRow = row ?? {
      uuid: data.uuid,
      status: data.status,
      merchant: data.merchant,
      transaction_date: data.transaction_date,
      amount: data.amount,
      confidence_score: 0,
      suggested_match: null,
      no_match_reason: null,
      retry_expires_at: null,
    };
    const reasonCopy = getReasonCopy(reasonRow, now, l10n);

    return (
      <div className={styles["drawer-body"]}>
        <h2 className={`${styles.merchant} fontFamily--heading`}>
          {data.merchant ?? l10n.getString("receipts-drawer-merchant")}
        </h2>
        <ReceiptImagePreview
          imageUrl={data.image_url}
          mimeType={data.image_mime_type}
          thumbnailUrl={data.thumbnail_url}
          largeThumbnailUrl={data.large_thumbnail_url}
        />
        <ReceiptDetailsCard
          merchant={data.merchant}
          transactionDate={data.transaction_date}
          amount={data.amount}
        />
        <div>
          <p className="nds-typescale--upper fontColor--secondary fontWeight--bold fontSize--xs margin--bottom--xs">
            {l10n.getString("receipts-drawer-suggested-match")}
          </p>
          <div
            className={`${styles.card} ${styles["reason-card"]} border--all rounded--all--m`}
            data-testid="unmatched-receipt-drawer-reason"
          >
            <p className="fontColor--primary">{reasonCopy}</p>
          </div>
        </div>
      </div>
    );
  };

  const footer = data ? (
    <div className={styles["footer-actions"]}>
      <Button
        as="a"
        kind="primary"
        startIcon="download"
        href={data.download_url}
        // Cross-origin S3: the download attribute is just a hint; the actual
        // filename comes from Content-Disposition baked into download_url.
        download={data.original_filename}
        label={l10n.getString("receipts-action-download")}
        testId="unmatched-receipt-drawer-download"
      />
    </div>
  ) : null;

  return (
    <Drawer
      isOpen
      onUserDismiss={onClose}
      onPrev={handlePrev}
      onNext={handleNext}
      position="right"
      depth="580px"
      paddingSize="xl"
      footer={footer}
      testId="unmatched-receipt-drawer"
    >
      {renderBody()}
    </Drawer>
  );
};

const UnmatchedReceiptDrawer = ({
  uuid,
  uuids,
  onSelect,
  row,
  now,
  onClose,
}: UnmatchedReceiptDrawerProps) => {
  // Mount-gated so query state tears down on close — keeps presigned S3 URLs
  // (5-minute TTL) out of long-lived React Query cache entries.
  if (uuid === null) {
    return null;
  }

  return (
    <OpenDrawer
      uuid={uuid}
      uuids={uuids}
      onSelect={onSelect}
      row={row}
      now={now ?? new Date()}
      onClose={onClose}
    />
  );
};

export default UnmatchedReceiptDrawer;
